/**
 * DictionaryFormModal — F12.N T5.3 (Settings → Słowniki), rozszerzone o autorstwo
 * LOKALNEGO słownika SQL tenanta (radio ręczny/SQL + edytor zapytania + „Testuj").
 *
 * Modal create/edit dla słownika:
 * - `mode='create'` → wszystkie pola edytowalne
 * - `mode='edit'` → kod readonly (immutable po stronie backendu)
 *
 * Sekcja SQL (przełącznik źródła + pole SELECT + deklaracje parametrów + „Testuj") jest
 * widoczna WYŁĄCZNIE gdy `canManageSql` (uprawnienie `crm_core.dictionaries.manage_sql`).
 * Bez tego uprawnienia modal działa jak dawniej — tylko metadane słownika ręcznego.
 *
 * Kontrakt na drut (mirror `GlobalDictionaryFormModal`, ale bez wariantu rejestru i bez
 * wyboru tenanta — test leci na WŁASNEJ bazie tenanta): w formularzu `sourceKind` to
 * `'manual' | 'sql'`; caller (`DictionariesTab`) wysyła je wprost do POST/PUT (`'sql'`, nie
 * wire `'external'`). Na EDYCJI treść zapytania i deklaracje parametrów są prefillowane z
 * `initialValues` (ładowanych przez rodzica z `GET /api/dictionaries/:code`, ujawniane tylko
 * posiadaczowi `manage_sql`).
 *
 * Walidacja:
 * - code: lowercase, [a-z][a-z0-9_]*, 2-50 znaków
 * - name: 1-100 znaków
 * - description: max 2000 znaków, opcjonalne
 * - parametry SQL: nazwa [a-zA-Z_][a-zA-Z0-9_]*, unikalna (case-insensitive)
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { InsertExampleButton } from '@/components/InsertExampleButton';
import { Modal } from '@/components/Modal';
import { RadioGroup } from '@/components/RadioGroup';
import { Textarea } from '@/components/Textarea';
import { SqlField } from '@/components/ui/SqlField';
import {
  DICTIONARY_PARAM_TYPES,
  type DictionaryParam,
  type DictionaryParamType,
  useTestDictionarySql,
} from '@/hooks/useDictionary';
import { buildSqlParamCompletions } from '@/lib/sql-editor/params';
import { useTenantSqlSchema } from '@/lib/sql-editor/schema';
import { useSqlTestDiagnostics } from '@/lib/sql-editor/test-error';

export type DictionaryDisplayMode = 'label' | 'code' | 'code_label';

/** Rodzaj źródła w FORMULARZU — wartość BAZY (`'sql'`), nie wire `'external'`. */
export type DictionaryFormSourceKind = 'manual' | 'sql';

export interface DictionaryFormValues {
  code: string;
  name: string;
  description?: string;
  displayMode: DictionaryDisplayMode;
  /** 'manual' — lista ręczna; 'sql' — pozycje wyliczane z zapytania SELECT. */
  sourceKind: DictionaryFormSourceKind;
  /** Treść zapytania SELECT — istotna tylko dla `sourceKind='sql'` (inaczej ''). */
  sourceSql: string;
  /** Deklaracje parametrów zapytania (`:nazwa`) — puste dla 'manual'. */
  declaredParams: DictionaryParam[];
}

const DISPLAY_MODES: DictionaryDisplayMode[] = ['label', 'code', 'code_label'];

/** Nazwa parametru SQL — identyfikator jak w `:nazwa` (mirror walidatora backendu). */
const PARAM_NAME_REGEX = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: DictionaryFormValues;
  /** Czy pokazać sekcję autorstwa SQL (uprawnienie `crm_core.dictionaries.manage_sql`). */
  canManageSql?: boolean;
  saving?: boolean;
  /**
   * Błędy zwrócone przez ostatnią próbę zapisu (np. 409 kolizja kodu, 400 błąd SQL),
   * pokazywane inline przy odpowiednim polu — analogicznie do `DictionaryItemFormModal`.
   * Caller ustawia nowy obiekt (nawet o tej samej treści) przy każdej próbie, żeby
   * `useEffect` poniżej zawsze wykrył zmianę i odświeżył widoczny błąd.
   */
  serverErrors?: Record<string, string>;
  onSubmit: (values: DictionaryFormValues) => void;
  onClose: () => void;
}

const CODE_REGEX = /^[a-z][a-z0-9_]*$/;

/**
 * Mapuje kod błędu z `POST /api/dictionaries/test-sql` na klucz i18n czytelnego komunikatu.
 * `SQL_EXECUTION_FAILED` obsługiwany osobno (niesie surowy komunikat Postgresa). Nieznany kod →
 * generyczny komunikat.
 */
function testSqlErrorKey(code: string | undefined): string {
  switch (code) {
    case 'SQL_NOT_SELECT':
      return 'tenant.dictionaries.form.sqlErrors.notSelect';
    case 'SQL_MULTIPLE_STATEMENTS':
      return 'tenant.dictionaries.form.sqlErrors.multipleStatements';
    case 'SQL_PARAM_UNDECLARED':
      return 'tenant.dictionaries.form.sqlErrors.paramUndeclared';
    case 'SQL_EMPTY':
      return 'tenant.dictionaries.form.sqlErrors.empty';
    case 'SQL_FORBIDDEN_KEYWORD':
      return 'tenant.dictionaries.form.sqlErrors.forbiddenKeyword';
    case 'SQL_UNSUPPORTED_SYNTAX':
      return 'tenant.dictionaries.form.sqlErrors.unsupportedSyntax';
    case 'EXTERNAL_PARAM_MISSING':
      return 'tenant.dictionaries.form.sqlErrors.paramMissing';
    case 'EXTERNAL_PARAM_INVALID':
      return 'tenant.dictionaries.form.sqlErrors.paramInvalid';
    case 'EXTERNAL_SQL_RESULT_SHAPE':
      return 'tenant.dictionaries.form.sqlErrors.resultShape';
    default:
      return 'tenant.dictionaries.form.sqlErrors.generic';
  }
}

export function DictionaryFormModal({
  open,
  mode,
  initialValues,
  canManageSql,
  saving,
  serverErrors,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState(initialValues.code);
  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description ?? '');
  const [displayMode, setDisplayMode] = useState<DictionaryDisplayMode>(initialValues.displayMode);
  const [sourceKind, setSourceKind] = useState<DictionaryFormSourceKind>(initialValues.sourceKind);
  const [sourceSql, setSourceSql] = useState(initialValues.sourceSql);
  const [declaredParams, setDeclaredParams] = useState<DictionaryParam[]>(
    initialValues.declaredParams,
  );
  // Sekcja „Testuj" — przykładowe wartości parametrów (nazwa → tekst wpisany przez autora).
  const [testParamValues, setTestParamValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const testMutation = useTestDictionarySql();

  const sqlActive = canManageSql === true && sourceKind === 'sql';
  const sqlSchema = useTenantSqlSchema(open && sqlActive);
  const sqlDiagnostics = useSqlTestDiagnostics(sourceSql, testMutation);
  const sqlParams = useMemo(
    () => buildSqlParamCompletions(t, [], declaredParams),
    [t, declaredParams],
  );

  useEffect(() => {
    if (open) {
      setCode(initialValues.code);
      setName(initialValues.name);
      setDescription(initialValues.description ?? '');
      setDisplayMode(initialValues.displayMode);
      setSourceKind(initialValues.sourceKind);
      setSourceSql(initialValues.sourceSql);
      setDeclaredParams(initialValues.declaredParams);
      setTestParamValues({});
      testMutation.reset();
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValues]);

  useEffect(() => {
    if (serverErrors) {
      setErrors((prev) => ({ ...prev, ...serverErrors }));
    }
  }, [serverErrors]);

  // „Zmieniono" dla zabezpieczenia zamknięcia: tryb wyświetlania, rodzaj źródła i parametry SQL
  // wybiera się kliknięciem (bez zdarzeń wpisywania), więc porównujemy wartości formularza ze
  // stanem startowym. Wartości próbne sekcji „Testuj" nie są danymi słownika.
  const isDirty =
    JSON.stringify([
      code,
      name,
      description,
      displayMode,
      sourceKind,
      sourceSql,
      declaredParams,
    ]) !==
    JSON.stringify([
      initialValues.code,
      initialValues.name,
      initialValues.description ?? '',
      initialValues.displayMode,
      initialValues.sourceKind,
      initialValues.sourceSql,
      initialValues.declaredParams,
    ]);

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (mode === 'create') {
      if (!code) next.code = t('tenant.dictionaries.form.errors.codeRequired');
      else if (!CODE_REGEX.test(code)) next.code = t('tenant.dictionaries.form.errors.codeInvalid');
      else if (code.length < 2 || code.length > 50)
        next.code = t('tenant.dictionaries.form.errors.codeLength');
    }
    if (!name.trim()) next.name = t('tenant.dictionaries.form.errors.nameRequired');
    else if (name.length > 100) next.name = t('tenant.dictionaries.form.errors.nameLength');
    if (description.length > 2000)
      next.description = t('tenant.dictionaries.form.errors.descriptionLength');
    if (sqlActive) {
      if (!sourceSql.trim()) {
        next.sourceSql = t('tenant.dictionaries.form.errors.sqlRequired');
      }
      const names = declaredParams.map((p) => p.name.trim());
      if (names.some((n) => !PARAM_NAME_REGEX.test(n))) {
        next.declaredParams = t('tenant.dictionaries.form.errors.sqlParamNameInvalid');
      } else if (new Set(names.map((n) => n.toLowerCase())).size !== names.length) {
        next.declaredParams = t('tenant.dictionaries.form.errors.sqlParamNameDuplicate');
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      displayMode,
      sourceKind: sqlActive ? 'sql' : 'manual',
      sourceSql: sqlActive ? sourceSql.trim() : '',
      declaredParams: sqlActive ? declaredParams.map((p) => ({ ...p, name: p.name.trim() })) : [],
    });
  }

  function updateParam(index: number, patch: Partial<DictionaryParam>) {
    setDeclaredParams((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }

  function removeParam(index: number) {
    setDeclaredParams((prev) => prev.filter((_, i) => i !== index));
  }

  function handleTest() {
    if (!sourceSql.trim()) return;
    const paramValues: Record<string, string> = {};
    for (const param of declaredParams) {
      const raw = testParamValues[param.name];
      if (raw !== undefined && raw !== '') paramValues[param.name] = raw;
    }
    testMutation.mutate({
      sourceSql: sourceSql.trim(),
      declaredParams: declaredParams.map((p) => ({ ...p, name: p.name.trim() })),
      ...(Object.keys(paramValues).length > 0 ? { paramValues } : {}),
    });
  }

  const testError = testMutation.isError
    ? (() => {
        const err = testMutation.error;
        const data =
          isAxiosError(err) && err.response?.data && typeof err.response.data === 'object'
            ? (err.response.data as { error?: string; paramName?: string; message?: string })
            : null;
        // SQL_EXECUTION_FAILED niesie surowy komunikat Postgresa — pokazujemy go wprost (autor ma
        // uprawnienie autoringu i dostęp do własnej bazy; komunikat silnika to treść dla niego).
        if (data?.error === 'SQL_EXECUTION_FAILED' && data.message) {
          return t('tenant.dictionaries.form.sqlErrors.executionFailed', { message: data.message });
        }
        return t(testSqlErrorKey(data?.error ?? undefined), { paramName: data?.paramName ?? '' });
      })()
    : null;
  const testResult = testMutation.data ?? null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        mode === 'create'
          ? t('tenant.dictionaries.form.titleCreate')
          : t('tenant.dictionaries.form.titleEdit')
      }
      // Słownik SQL: szerokie okno — dane słownika w dwóch kolumnach, pod nimi pole SQL na całą
      // szerokość, na dole parametry obok „Testuj” (bez przewijania); ręczny zostaje wąski.
      size={sqlActive ? '2xl' : 'md'}
      testId="dictionary-form-modal"
      guardUnsaved
      dirty={isDirty}
      // W trakcie zapisu tło i Esc nie zamykają okna („Anuluj" jest wyłączone; X w nagłówku zostaje).
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            data-testid="dictionary-form-submit"
          >
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </>
      }
    >
      <div
        className={
          sqlActive ? 'grid grid-cols-1 items-start gap-x-6 gap-y-3 lg:grid-cols-2' : 'space-y-3'
        }
        data-testid="dictionary-form-layout"
      >
        <div className="space-y-3">
          <FormField label={t('tenant.dictionaries.form.code')} error={errors.code}>
            <Input
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setErrors((prev) => ({ ...prev, code: '' }));
              }}
              disabled={mode === 'edit'}
              placeholder="np. project_status"
              data-testid="dictionary-form-code"
            />
          </FormField>
          <FormField label={t('tenant.dictionaries.form.name')} error={errors.name}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="np. Status projektu"
              data-testid="dictionary-form-name"
            />
          </FormField>
          <FormField
            label={t('tenant.dictionaries.form.description')}
            error={errors.description}
            hint={t('tenant.dictionaries.form.descriptionHint')}
          >
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="dictionary-form-description"
            />
          </FormField>
        </div>

        <div className="space-y-3">
          <FormField label={t('tenant.dictionaries.form.displayMode')}>
            <Dropdown
              value={displayMode}
              onChange={(v) => setDisplayMode(v as DictionaryDisplayMode)}
              data-testid="dictionary-form-display-mode"
              options={DISPLAY_MODES.map((mode) => ({
                value: mode,
                label: t(`tenant.dictionaries.displayMode.${mode}`),
              }))}
            />
          </FormField>

          {canManageSql && (
            <RadioGroup<DictionaryFormSourceKind>
              name="dictionary-source-kind"
              legend={t('tenant.dictionaries.form.sourceKind')}
              value={sourceKind}
              onChange={(value) => {
                setSourceKind(value);
                setErrors((prev) => ({ ...prev, sourceSql: '', declaredParams: '' }));
                if (value === 'manual') testMutation.reset();
              }}
              options={[
                {
                  value: 'manual',
                  label: t('tenant.dictionaries.form.sourceKindManual'),
                },
                {
                  value: 'sql',
                  label: t('tenant.dictionaries.form.sourceKindSql'),
                },
              ]}
              data-testid="dictionary-form-source-kind"
            />
          )}
        </div>

        {sqlActive && (
          <>
            {/* Pole SQL na całą szerokość okna (obie kolumny). */}
            <div className="space-y-1 lg:col-span-2">
              <div className="flex justify-end">
                <InsertExampleButton
                  currentValue={sourceSql}
                  example={t('tenant.dictionaries.form.sqlExample')}
                  onInsert={setSourceSql}
                  data-testid="dictionary-form-sql-example"
                />
              </div>
              <FormField
                label={t('tenant.dictionaries.form.sqlLabel')}
                hint={t('tenant.dictionaries.form.sqlHint')}
                error={errors.sourceSql}
              >
                <SqlField
                  value={sourceSql}
                  onChange={(value) => {
                    setSourceSql(value);
                    setErrors((prev) => ({ ...prev, sourceSql: '' }));
                  }}
                  schema={sqlSchema}
                  parameters={sqlParams}
                  diagnostics={sqlDiagnostics}
                  placeholder={t('common.examplePlaceholder', {
                    example: t('tenant.dictionaries.form.sqlExample'),
                  })}
                  aria-label={t('tenant.dictionaries.form.sqlLabel')}
                  minHeight="200px"
                  data-testid="dictionary-form-sql"
                />
              </FormField>
            </div>

            <FormField
              label={t('tenant.dictionaries.form.sqlParamsTitle')}
              hint={t('tenant.dictionaries.form.sqlParamsHint')}
              error={errors.declaredParams}
            >
              <div className="space-y-2" data-testid="dictionary-form-sql-params">
                {declaredParams.map((param, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={param.name}
                      onChange={(e) => {
                        updateParam(index, { name: e.target.value });
                        setErrors((prev) => ({ ...prev, declaredParams: '' }));
                      }}
                      placeholder={t('tenant.dictionaries.form.sqlParamNamePlaceholder')}
                      containerClassName="min-w-0 flex-1"
                      data-testid={`dictionary-form-sql-param-name-${index}`}
                    />
                    <Dropdown
                      className="w-32 shrink-0"
                      value={param.type}
                      onChange={(value) =>
                        updateParam(index, { type: value as DictionaryParamType })
                      }
                      options={DICTIONARY_PARAM_TYPES.map((type) => ({
                        value: type,
                        label: t(`tenant.dictionaries.form.paramType.${type}`),
                      }))}
                      data-testid={`dictionary-form-sql-param-type-${index}`}
                    />
                    <Checkbox
                      checked={param.required}
                      onChange={(e) => updateParam(index, { required: e.target.checked })}
                      label={t('tenant.dictionaries.form.paramRequired')}
                      containerClassName="shrink-0"
                      data-testid={`dictionary-form-sql-param-required-${index}`}
                    />
                    <IconButton
                      onClick={() => removeParam(index)}
                      aria-label={t('tenant.dictionaries.form.sqlParamRemove')}
                      variant="danger"
                      size="sm"
                      data-testid={`dictionary-form-sql-param-remove-${index}`}
                    >
                      <Trash2 />
                    </IconButton>
                  </div>
                ))}
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Plus className="h-3.5 w-3.5" />}
                  onClick={() =>
                    setDeclaredParams((prev) => [
                      ...prev,
                      { name: '', type: 'text', required: false },
                    ])
                  }
                  data-testid="dictionary-form-sql-param-add"
                >
                  {t('tenant.dictionaries.form.sqlParamAdd')}
                </Button>
              </div>
            </FormField>

            {/* Sekcja „Testuj" — próbne wykonanie na WŁASNEJ bazie tenanta (rola RO). */}
            <div
              className="space-y-2 rounded-lg border border-border-primary bg-bg-secondary p-3"
              data-testid="dictionary-form-test-section"
            >
              <p className="text-sm font-medium text-text-primary">
                {t('tenant.dictionaries.form.testTitle')}
              </p>
              {declaredParams.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-text-muted">
                    {t('tenant.dictionaries.form.testParamValuesHint')}
                  </p>
                  {declaredParams
                    .filter((param) => param.name.trim())
                    .map((param) => (
                      <FormField key={param.name} label={param.name}>
                        <Input
                          value={testParamValues[param.name] ?? ''}
                          onChange={(e) =>
                            setTestParamValues((prev) => ({
                              ...prev,
                              [param.name]: e.target.value,
                            }))
                          }
                          data-testid={`dictionary-form-test-param-${param.name}`}
                        />
                      </FormField>
                    ))}
                </div>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={handleTest}
                loading={testMutation.isPending}
                disabled={testMutation.isPending || sourceSql.trim().length === 0}
                data-testid="dictionary-form-test-btn"
              >
                {t('tenant.dictionaries.form.testButton')}
              </Button>

              {testError && (
                <p
                  role="alert"
                  className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
                  data-testid="dictionary-form-test-error"
                >
                  {testError}
                </p>
              )}

              {testResult && (
                <div className="space-y-2" data-testid="dictionary-form-test-result">
                  <p className="text-xs text-text-secondary">
                    {t('tenant.dictionaries.form.testRowCount', { count: testResult.rowCount })}
                  </p>
                  {testResult.truncated && (
                    <p
                      className="text-xs text-amber-600 dark:text-amber-400"
                      data-testid="dictionary-form-test-truncated"
                    >
                      {t('tenant.dictionaries.form.testTruncated')}
                    </p>
                  )}
                  {testResult.rows.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="border-b border-border-primary text-left text-text-muted">
                          <tr>
                            {testResult.columns.map((column) => (
                              <th key={column} className="px-2 py-1 font-medium">
                                {column}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-primary">
                          {testResult.rows.slice(0, 20).map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {testResult.columns.map((column) => (
                                <td key={column} className="px-2 py-1 text-text-secondary">
                                  {row[column] === null || row[column] === undefined
                                    ? '—'
                                    : String(row[column])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
